import { useEffect, useMemo, useState } from "react";
import type {
  Hasher,
  Trail,
  TrailHistoryEntry,
  UpdateTrailDetailsRequest,
} from "@hash/shared";
import { STANDARD_BRING_ITEMS, trailStartAt } from "@hash/shared";
import { api } from "../api/client";
import { parseApiError } from "../api/errors";
import { loadHashers, searchHashers, useHashers } from "../stores/hasherStore";
import { getClaims } from "../auth/cognito";

interface Props {
  trailId: string;
  // Current user — gates editing and labels their hare note.
  hasherId: string;
  onClose: () => void;
}

/**
 * Trail details editor (Stories 7.1–7.3): the prep info hares set for a trail.
 * The full details are editable only while a hare is assigned; the title can
 * also be set by a misman on a hareless placeholder. Saves are optimistically
 * locked (7.2) — a stale version surfaces an overwrite/reload banner — and every save
 * is recorded in the history view (7.3).
 */
export function TrailDetailsPage({ trailId, hasherId, onClose }: Props) {
  const [trail, setTrail] = useState<Trail | null>(null);
  const [form, setForm] = useState<UpdateTrailDetailsRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<Trail | null>(null);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState<TrailHistoryEntry[] | null>(null);
  // Prices are edited as dollar strings (same idiom as the Kennels fee
  // dialogs) and converted to integer cents at save.
  const [memberStr, setMemberStr] = useState("");
  const [nonMemberStr, setNonMemberStr] = useState("");
  const [hareQuery, setHareQuery] = useState("");
  // Hare search runs against the shared client-side store.
  const { hashers: roster } = useHashers();
  // id -> { name, homeKennel }, for rendering the assigned-hares list (the trail
  // row only stores ids). Seeded from GET /trails/:id's hashers and kept current
  // as hares are assigned. The kennel disambiguates same-named hashers.
  const [hareNames, setHareNames] = useState<
    Record<string, { name: string; homeKennel: string }>
  >({});
  const [confirmReset, setConfirmReset] = useState(false);
  // Custom hare listing toggle (free text stands in for hasher hares). Seeded
  // from the loaded trail in hydrate(); the text itself lives in form.customHare.
  const [customHareOn, setCustomHareOn] = useState(false);
  // Date + start-time edit (misman/admin only) — folded into the single Save
  // below, and usable even on a hare-less placeholder trail. The kennel's
  // schedule time zone is what we resolve the wall-clock against.
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [kennelTz, setKennelTz] = useState<string | undefined>(undefined);

  const hydrate = (t: Trail) => {
    setTrail(t);
    setDate(t.date);
    setStartTime(hhmmOf(t.startAt));
    setMemberStr(t.memberPrice == null ? "" : (t.memberPrice / 100).toFixed(2));
    setNonMemberStr(
      t.nonMemberPrice == null ? "" : (t.nonMemberPrice / 100).toFixed(2)
    );
    setForm({
      version: t.version ?? 0,
      startLocation: t.startLocation ?? "",
      startLocationUrl: t.startLocationUrl ?? "",
      transit: t.transit ?? "",
      title: t.title ?? "",
      description: t.description ?? "",
      onIn: t.onIn ?? "",
      aa: t.aa ?? false,
      bringStandard: t.bringStandard ?? [],
      bringCustom: t.bringCustom ?? [],
      specialFee: t.specialFee ?? false,
      hareNotes: { ...(t.hareNotes ?? {}) },
      customHare: t.customHare ?? "",
    });
    setCustomHareOn(!!t.customHare?.trim());
  };

  useEffect(() => {
    api.trails
      .get(trailId)
      .then(({ trail, hashers, kennel }) => {
        hydrate(trail);
        setKennelTz(kennel?.schedule?.timeZone);
        setHareNames((m) => ({
          ...m,
          ...Object.fromEntries(
            hashers.map((h) => [h.id, { name: h.name, homeKennel: h.homeKennel }])
          ),
        }));
      })
      .catch((e) => setError(parseApiError(e).message));
    // Ensure the roster store is loaded (and refreshed if stale) for hare search.
    loadHashers().catch(() => {});
  }, [trailId]);

  // Hasher search for assigning hares, over the local store. Any hasher can be
  // a hare — including lite/unclaimed accounts created at a trail — matching the
  // picker in NewTrail. The server enforces hare/misman scope on assignment.
  const hareResults = useMemo(
    () => searchHashers(hareQuery),
    [hareQuery, roster]
  );

  if (!form || !trail) {
    return <p className="px-4 py-6 text-sm text-slate-500">Loading…</p>;
  }

  // A custom hare listing (in the live form) counts as having a hare: it opens
  // the full prep details, mirroring the server gate. server enforces hare/misman scope.
  const hasCustomHare = !!(form.customHare ?? "").trim();
  const canEdit = (trail.hareHasherIds?.length ?? 0) > 0 || hasCustomHare;
  // Mismanagement of this trail's kennel (admin, or misman of the kennel). Gates
  // misman-only acts: date/time editing, and editing *another* hare's note. The
  // server re-enforces the scope.
  const claims = getClaims();
  const isMisman =
    claims?.role === "admin" ||
    (claims?.mismanKennels ?? []).includes(trail.kennelId);
  const canEditTime = isMisman;
  // The title is editable with the full details once a hare is assigned, but a
  // misman/admin can also name a hareless placeholder trail (server-enforced).
  const canEditTitle = canEdit || canEditTime;
  const set = <K extends keyof UpdateTrailDetailsRequest>(
    k: K,
    v: UpdateTrailDetailsRequest[K]
  ) => setForm((f) => (f ? { ...f, [k]: v } : f));

  // A hare's note is keyed by their hasher id. A blank value drops the key so
  // the map stays sparse (and the public projection omits empty notes).
  const setHareNote = (id: string, v: string) =>
    setForm((f) => {
      if (!f) return f;
      const next = { ...(f.hareNotes ?? {}) };
      if (v.trim()) next[id] = v;
      else delete next[id];
      return { ...f, hareNotes: next };
    });

  const toggleStandard = (key: string) =>
    set(
      "bringStandard",
      (form.bringStandard ?? []).includes(key)
        ? (form.bringStandard ?? []).filter((k) => k !== key)
        : [...(form.bringStandard ?? []), key]
    );

  // "" -> undefined; otherwise dollars -> integer cents (NaN/negative -> undefined).
  const toCents = (s: string): number | undefined => {
    const t = s.trim();
    if (!t) return undefined;
    const n = Number(t);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : undefined;
  };

  const save = async (overwrite = false) => {
    const memberPrice = toCents(memberStr);
    const nonMemberPrice = toCents(nonMemberStr);
    // Client mirror of the special-fee invariant (server re-checks).
    if (form.specialFee) {
      if (memberPrice == null || nonMemberPrice == null) {
        setError("Set both member and non-member prices.");
        return;
      }
      if (memberPrice > nonMemberPrice) {
        setError("Member price must be ≤ non-member price.");
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      // Misman/admin: persist date/time edits first (a separate, misman-only
      // endpoint that enforces the strict N−1/N+1 ordering). Skip it when
      // nothing changed so an untouched past trail doesn't trip the rule.
      if (canEditTime) {
        if (!date) {
          setError("Pick a date.");
          setSaving(false);
          return;
        }
        const startAt = startTime
          ? trailStartAt(date, startTime, kennelTz) ?? undefined
          : undefined;
        const dateTimeChanged =
          date !== trail.date || (startAt ?? "") !== (trail.startAt ?? "");
        if (dateTimeChanged) {
          // Keep the trail row (and its version) current, but don't re-hydrate
          // the form — that would discard any in-progress detail edits.
          setTrail(await api.trails.update(trailId, { date, startAt }));
        }
      }
      // Details save (optimistically locked). With a hare, the full form goes
      // up; on a hareless placeholder a misman can still set just the title.
      if (canEdit) {
        const updated = await api.trails.updateDetails(trailId, {
          ...form,
          memberPrice,
          nonMemberPrice,
          overwrite,
        });
        hydrate(updated);
      } else if (canEditTitle) {
        // Hareless placeholder: a misman can still set the title and/or the
        // custom hare listing (including clearing it back to empty).
        const titleChanged = (form.title ?? "") !== (trail.title ?? "");
        const customHareChanged =
          (form.customHare ?? "") !== (trail.customHare ?? "");
        if (titleChanged || customHareChanged) {
          const updated = await api.trails.updateDetails(trailId, {
            version: form.version,
            ...(titleChanged ? { title: form.title } : {}),
            ...(customHareChanged ? { customHare: form.customHare } : {}),
            overwrite,
          });
          hydrate(updated);
        }
      }
      setConflict(null);
      onClose();
    } catch (e) {
      const apiErr = parseApiError(e);
      const conflictBody = apiErr.body as { current?: Trail } | undefined;
      if (
        apiErr.status === 409 &&
        apiErr.code === "VERSION_CONFLICT" &&
        conflictBody?.current
      ) {
        // Surface the diff source so the hare can overwrite or reload (7.2).
        setConflict(conflictBody.current);
      } else {
        setError(apiErr.message);
      }
      setSaving(false);
    }
  };

  const assignHare = async (h: Hasher) => {
    try {
      setTrail(await api.trails.assignHare(trailId, h.id));
      setHareNames((m) => ({
        ...m,
        [h.id]: { name: h.name, homeKennel: h.homeKennel },
      }));
      setHareQuery("");
    } catch (e) {
      setError(parseApiError(e).message);
    }
  };
  const removeHare = async (id: string) => {
    try {
      setTrail(await api.trails.removeHare(trailId, id));
    } catch (e) {
      setError(parseApiError(e).message);
    }
  };
  const reset = async () => {
    try {
      hydrate(await api.trails.reset(trailId));
      setConfirmReset(false);
    } catch (e) {
      setError(parseApiError(e).message);
    }
  };

  return (
    <div className="min-h-screen pb-24">
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
        <button onClick={onClose} className="text-sm text-slate-500">
          ← Check-in
        </button>
        <h2 className="text-base font-bold">Trail details</h2>
        <button
          onClick={() =>
            api.trails.history(trailId).then((r) => setHistory(r.history))
          }
          className="text-sm text-slate-500"
        >
          History
        </button>
      </header>

      {!canEdit && (
        <p className="bg-amber-50 px-4 py-2 text-sm text-amber-800">
          {canEditTitle
            ? "Assign a hare to edit the full details — the title can be set now."
            : "Assign a hare before editing details."}
        </p>
      )}

      {conflict && (
        <div className="m-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          This trail was changed by someone else (now v{conflict.version}).
          <div className="mt-2 flex gap-2">
            <button
              onClick={() => save(true)}
              className="rounded-lg bg-amber-700 px-3 py-1.5 font-medium text-white"
            >
              Overwrite
            </button>
            <button
              onClick={() => {
                hydrate(conflict);
                setConflict(null);
              }}
              className="rounded-lg bg-white px-3 py-1.5 font-medium text-amber-800 ring-1 ring-amber-300"
            >
              Reload theirs
            </button>
          </div>
        </div>
      )}

      {/* Date is shown to everyone but only a misman/admin can change it (and
          the start time). Both fold into the single Save below. */}
      <section className="border-b border-slate-100 px-4 py-4 space-y-3">
        <label className="block">
          <Label>Date</Label>
          <input
            type="date"
            value={date}
            disabled={!canEditTime}
            onChange={(e) => setDate(e.target.value)}
            aria-label="Date"
            className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900 disabled:bg-slate-50 disabled:text-slate-500"
          />
        </label>
        {canEditTime && (
          <label className="block">
            <Label>Start time</Label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              aria-label="Start time"
              className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
            <p className="mt-1 text-xs text-slate-400">
              Defaults from the kennel schedule; override it for this trail.
            </p>
          </label>
        )}
      </section>

      {/* Title sits outside the hare-gated fieldset: a misman can name a
          hareless placeholder trail before any hare is assigned. */}
      <fieldset
        disabled={!canEditTitle}
        className="border-b border-slate-100 px-4 py-4"
      >
        <Text label="Title" value={form.title ?? ""} onChange={(v) => set("title", v)} />
      </fieldset>

      <fieldset disabled={!canEdit} className="space-y-3 px-4 py-4">
        <Area label="Description" value={form.description ?? ""} onChange={(v) => set("description", v)} />
        <Text label="Start location" value={form.startLocation ?? ""} onChange={(v) => set("startLocation", v)} />
        <Text label="Start location link" value={form.startLocationUrl ?? ""} onChange={(v) => set("startLocationUrl", v)} />
        <Text label="Transit" value={form.transit ?? ""} onChange={(v) => set("transit", v)} />

        <Toggle label="A-A (no on-in)" checked={!!form.aa} onChange={(v) => set("aa", v)} />
        {!form.aa && (
          <Text label="On-in location" value={form.onIn ?? ""} onChange={(v) => set("onIn", v)} />
        )}

        <div>
          <Label>What to bring</Label>
          <div className="flex flex-wrap gap-2">
            {STANDARD_BRING_ITEMS.map((item) => {
              const on = (form.bringStandard ?? []).includes(item.key);
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => toggleStandard(item.key)}
                  aria-pressed={on}
                  className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                    on ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <Toggle label="Special fee" checked={!!form.specialFee} onChange={(v) => set("specialFee", v)} />
        {form.specialFee && (
          <div className="grid grid-cols-2 gap-2">
            <Money label="Member" value={memberStr} onChange={setMemberStr} />
            <Money label="Non-member" value={nonMemberStr} onChange={setNonMemberStr} />
          </div>
        )}
      </fieldset>

      {/* Custom hare listing: free text that stands in for hasher hares. A
          misman can set it on a hareless placeholder; once set it counts as a
          hare and opens the full details above. Shown as the hares everywhere. */}
      <fieldset
        disabled={!canEditTitle}
        className="space-y-2 border-b border-slate-100 px-4 py-4"
      >
        <Toggle
          label="Custom hare listing"
          checked={customHareOn}
          onChange={(v) => {
            setCustomHareOn(v);
            if (!v) set("customHare", "");
          }}
        />
        {customHareOn && (
          <>
            <Text
              label="Hares (free text)"
              value={form.customHare ?? ""}
              onChange={(v) => set("customHare", v)}
            />
            <p className="text-xs text-slate-400">
              Shown as the hares everywhere instead of assigned hashers — no hare
              needs to be assigned.
            </p>
          </>
        )}
      </fieldset>

      <section className="px-4 py-2">
        <Label>Hares ({trail.hareHasherIds?.length ?? 0})</Label>
        <ul className="space-y-1">
          {(trail.hareHasherIds ?? []).map((id) => {
            // A note is editable by a misman (any hare) or by the hare it
            // belongs to (their own). It's shown publicly on the hareline.
            const canEditNote = isMisman || id === hasherId;
            return (
              <li
                key={id}
                className="space-y-1.5 rounded-lg bg-slate-50 px-3 py-2 text-sm"
              >
                <div className="flex items-center justify-between">
                  <span className="min-w-0 truncate text-slate-700">
                    {hareNames[id]?.name ?? id}
                    {hareNames[id]?.homeKennel && (
                      <span className="ml-1.5 text-xs text-slate-400">
                        {hareNames[id]!.homeKennel}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeHare(id)}
                    aria-label={`Remove hare ${hareNames[id]?.name ?? id}`}
                    className="text-rose-600"
                  >
                    Remove
                  </button>
                </div>
                <input
                  value={form.hareNotes?.[id] ?? ""}
                  disabled={!canEditNote}
                  onChange={(e) => setHareNote(id, e.target.value)}
                  placeholder={
                    canEditNote ? "Public note (e.g. Virgin Lay)" : undefined
                  }
                  aria-label={`Public note for ${hareNames[id]?.name ?? id}`}
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 disabled:bg-slate-100 disabled:text-slate-500"
                />
              </li>
            );
          })}
        </ul>
        {(trail.hareHasherIds?.length ?? 0) > 0 && (
          <p className="mt-1 text-xs text-slate-400">
            Hare notes appear publicly on the hareline.
          </p>
        )}
        <input
          type="search"
          placeholder="Add a hare (search by name)…"
          value={hareQuery}
          onChange={(e) => setHareQuery(e.target.value)}
          onFocus={() => loadHashers().catch(() => {})}
          aria-label="Add a hare"
          className="mt-2 h-11 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
        />
        {hareResults.length > 0 && (
          <ul className="mt-1 divide-y divide-slate-100 rounded-lg border border-slate-200">
            {hareResults.map((h) => (
              <li key={h.id}>
                <button
                  type="button"
                  onClick={() => assignHare(h)}
                  className="w-full px-3 py-2 text-left text-sm text-slate-900 hover:bg-slate-50"
                >
                  {h.name} <span className="text-slate-400">· {h.homeKennel}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {error && <p className="px-4 text-sm text-red-600" role="alert">{error}</p>}

      {/* Reset restores a schedule placeholder; special events are never
          scheduled, so there's nothing to reset (Milestone 9). */}
      <section className={`px-4 py-2 ${trail?.eventType === "special" ? "hidden" : ""}`}>
        {confirmReset ? (
          <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm">
            <p className="font-medium text-rose-800">
              Reset this trail? Removes all hares and wipes every manual edit.
              The number, date, and time are kept. This can't be undone.
            </p>
            <div className="mt-2 flex gap-2">
              <button
                onClick={reset}
                className="rounded-lg bg-rose-600 px-3 py-1.5 font-medium text-white"
              >
                Reset trail
              </button>
              <button
                onClick={() => setConfirmReset(false)}
                className="rounded-lg bg-white px-3 py-1.5 font-medium text-slate-700 ring-1 ring-slate-300"
              >
                Keep it
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setConfirmReset(true)}
            className="text-sm font-medium text-rose-600"
          >
            Reset trail…
          </button>
        )}
      </section>

      {(canEdit || canEditTime) && (
        <div className="flex gap-2 px-4 py-2">
          <button
            onClick={onClose}
            className="h-12 flex-1 rounded-lg bg-slate-100 font-medium text-slate-700"
          >
            Cancel
          </button>
          <button
            onClick={() => save(false)}
            disabled={saving}
            className="h-12 flex-1 rounded-lg bg-slate-900 font-medium text-white disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      )}

      {history && (
        <section className="px-4 py-4">
          <h3 className="text-sm font-semibold text-slate-700">Edit history</h3>
          {history.length === 0 ? (
            <p className="text-sm text-slate-400">No edits yet.</p>
          ) : (
            <ul className="mt-2 space-y-2">
              {history.map((h) => (
                <li key={h.at} className="rounded-lg bg-slate-50 p-2 text-sm">
                  <div className="text-slate-500">
                    v{h.version} · {new Date(h.at).toLocaleString()}
                  </div>
                  <div className="text-slate-700">
                    {h.changes.map((c) => c.field).join(", ")}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

// The "HH:mm" wall-clock embedded in startAt (for the <input type="time">),
// read straight off the ISO string so the viewer's zone can't shift it. "" when
// the trail is date-only.
function hhmmOf(startAt?: string): string {
  return startAt?.match(/T(\d{2}:\d{2})/)?.[1] ?? "";
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
      {children}
    </span>
  );
}

function Text({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
      />
    </label>
  );
}

function Area({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        rows={3}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
      />
    </label>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-3 text-left"
    >
      <span className="font-medium text-slate-900">{label}</span>
      <span
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full ${
          checked ? "bg-emerald-600" : "bg-slate-300"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </span>
    </button>
  );
}

function Money({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <Label>{label} ($)</Label>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0.00"
        aria-label={label}
        className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
      />
    </label>
  );
}
